import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus, UserPlus, Ban, KeyRound, Pencil } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  it_admin: "IT Technician",
  employee: "Employee",
  auditor: "Auditor",
};

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "employee", department: "", company_id: "" });

  const rolesAvailable = user?.role === "super_admin"
    ? ["super_admin", "company_admin", "it_admin", "employee", "auditor"]
    : ["company_admin", "it_admin", "employee", "auditor"];

  const load = async () => {
    const { data } = await api.get("/auth/users?include_inactive=true");
    setUsers(data);
  };

  useEffect(() => {
    load();
    if (user?.role === "super_admin") {
      api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  const resetForm = () => setForm({ email: "", full_name: "", password: "", role: "employee", department: "", company_id: "" });

  const openNew = () => { setEditing(null); resetForm(); setOpen(true); };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, full_name: u.full_name, password: "", role: u.role, department: u.department || "", company_id: u.company_id || "" });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        const payload = {
          full_name: form.full_name,
          role: form.role,
          department: form.department,
        };
        if (form.password) payload.password = form.password;
        if (user.role === "super_admin" && form.company_id) payload.company_id = form.company_id;
        await api.patch(`/auth/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password || !form.full_name) { toast.error("Email, name and password required"); return; }
        const payload = { ...form };
        if (user.role !== "super_admin") {
          delete payload.company_id;
        } else if (!payload.company_id && payload.role !== "super_admin") {
          toast.error("Select a company"); return;
        }
        await api.post("/auth/register", payload);
        toast.success("User added");
      }
      setOpen(false); resetForm(); setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.full_name}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("User deactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reactivate = async (u) => {
    try {
      await api.patch(`/auth/users/${u.id}`, { active: true });
      toast.success("User reactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.full_name}:`);
    if (!pw || pw.length < 4) { if (pw !== null) toast.error("Password too short"); return; }
    try {
      await api.patch(`/auth/users/${u.id}`, { password: pw });
      toast.success("Password reset");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const byRole = {};
  users.forEach(u => { byRole[u.role] = (byRole[u.role] || 0) + 1; });

  const companyName = (cid) => companies.find(c => c.id === cid)?.name || (cid ? "—" : "Global");

  return (
    <div className="p-8 space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>People</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Team & Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Invite technicians, employees, auditors. Assign roles and scope access per company.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu module="users" />
          <Button data-testid="new-user-btn" onClick={openNew} className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head">
            <UserPlus size={14} className="mr-1"/> Invite User
          </Button>
        </div>
      </div>

      {/* Role summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 border border-border bg-white">
        {["super_admin","company_admin","it_admin","employee","auditor"].map((r, i) => (
          <div key={r} className={`p-4 ${i < 4 ? "md:border-r" : ""} border-border`} data-testid={`role-count-${r}`}>
            <DataLabel>{ROLE_LABELS[r]}</DataLabel>
            <div className="font-head font-black text-3xl tracking-tighter mt-1">{byRole[r] || 0}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="border border-border bg-white overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              {["NAME","EMAIL","ROLE","COMPANY","DEPARTMENT","STATUS","ACTIONS"].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={`border-b border-border hover-row ${!u.active ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{u.full_name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{u.id.slice(0,8)}</div>
                </td>
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3">
                  <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-semibold bg-muted">{ROLE_LABELS[u.role] || u.role}</span>
                </td>
                <td className="p-3 text-xs">{companyName(u.company_id)}</td>
                <td className="p-3 text-xs">{u.department || "—"}</td>
                <td className="p-3">
                  {u.active ? (
                    <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#E8F4EC] text-[#0a7a3f]">ACTIVE</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-semibold bg-muted">DISABLED</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={13}/></button>
                    <button data-testid={`reset-pw-${u.id}`} onClick={() => resetPassword(u)} title="Reset password" className="hover:text-[#FF5000]"><KeyRound size={13}/></button>
                    {u.id !== user.id && (
                      u.active ? (
                        <button data-testid={`deactivate-${u.id}`} onClick={() => deactivate(u)} title="Deactivate" className="hover:text-[#FF3B30]"><Ban size={13}/></button>
                      ) : (
                        <button data-testid={`reactivate-${u.id}`} onClick={() => reactivate(u)} title="Reactivate" className="hover:text-[#0a7a3f] text-xs uppercase tracking-wider">enable</button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); resetForm(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit User" : "Invite New User"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="data-label">Full Name</Label>
              <Input data-testid="user-form-name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})}/></div>
            <div className="col-span-2"><Label className="data-label">Email</Label>
              <Input data-testid="user-form-email" className="font-mono" type="email" value={form.email} disabled={!!editing}
                     onChange={e => setForm({...form, email: e.target.value})}/>
              {editing && <div className="data-label mt-1">Email cannot be changed after creation.</div>}
            </div>
            <div><Label className="data-label">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger data-testid="user-form-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {rolesAvailable.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="data-label">Department</Label>
              <Input data-testid="user-form-dept" value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="e.g., IT, Finance"/></div>
            {user.role === "super_admin" && form.role !== "super_admin" && (
              <div className="col-span-2"><Label className="data-label">Company</Label>
                <Select value={form.company_id || "_none"} onValueChange={(v) => setForm({...form, company_id: v === "_none" ? "" : v})}>
                  <SelectTrigger data-testid="user-form-company"><SelectValue placeholder="Select company"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— (no company)</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label className="data-label">{editing ? "New Password (leave blank to keep)" : "Password"}</Label>
              <Input data-testid="user-form-password" className="font-mono" type="password" value={form.password}
                     onChange={e => setForm({...form, password: e.target.value})} placeholder={editing ? "••••••••" : "Set initial password"}/>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="user-form-submit" onClick={submit} className="bg-black text-white hover:bg-[#FF5000]">
              {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
